/**
 * CreateSiteEntryModal Component
 * Modal for manually creating a site database entry
 */

import React, { useState } from 'react';
import { siteDatabaseAPI } from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import { toast } from 'react-toastify';

const CreateSiteEntryModal = ({ isOpen, onClose, onSuccess, project, summary }) => {
  const [projectType, setProjectType] = useState('renovation');
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const progressPercentage = summary?.totalPercentagePaid || 0;
  const isLowPayment = progressPercentage < 50;

  // Handle submit
  const handleSubmit = async () => {
    if (isLowPayment && !confirmed) {
      toast.error('Please confirm you want to proceed with less than 50% payment');
      return;
    }

    setSubmitting(true);

    try {
      await siteDatabaseAPI.createFromProject(project.id, projectType);
      onSuccess();
    } catch (err) {
      console.error('Error creating site entry:', err);
      toast.error(err.message || 'Failed to create site entry');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-gray-500 bg-opacity-75 flex items-end sm:items-center justify-center">
      {/* Backdrop - only for desktop clicking outside */}
      <div
        className="absolute inset-0"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-lg sm:mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header - Sticky */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-blue-100">
              <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div className="min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                Create Site Entry
              </h3>
              <p className="text-sm text-gray-500 truncate">
                Project {project?.projectNumber}
              </p>
            </div>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="px-4 sm:px-6 py-4 overflow-y-auto flex-1">
          {/* Payment Summary */}
          {summary && (
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Payment Summary</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Package Price:</span>
                  <span className="font-medium">{formatCurrency(summary.totalPackagePrice)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Collected:</span>
                  <span className="font-medium text-green-600">{formatCurrency(summary.totalPaidAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Percentage:</span>
                  <span className={`font-medium ${progressPercentage >= 50 ? 'text-green-600' : 'text-orange-600'}`}>
                    {progressPercentage.toFixed(1)}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Milestones Completed:</span>
                  <span className="font-medium">{summary.completedMilestones}/{summary.milestoneCount}</span>
                </div>
              </div>

              {/* Progress bar */}
              <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${progressPercentage >= 50 ? 'bg-green-500' : 'bg-orange-500'}`}
                    style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          )}

          {/* Low Payment Warning */}
          {isLowPayment && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 mb-4">
              <div className="flex">
                <svg className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm text-yellow-700">
                    <strong>Warning:</strong> Only {progressPercentage.toFixed(1)}% payment has been collected.
                    Typically 50% is required before starting site work.
                  </p>
                  <label className="flex items-center mt-3 min-h-[44px] touch-manipulation">
                    <input
                      type="checkbox"
                      checked={confirmed}
                      onChange={(e) => setConfirmed(e.target.checked)}
                      className="h-5 w-5 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                    />
                    <span className="ml-3 text-sm text-yellow-700">
                      I understand and want to proceed anyway
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Project Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project Type
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setProjectType('renovation')}
                className={`p-4 border-2 rounded-lg text-left transition-colors min-h-[72px] touch-manipulation ${
                  projectType === 'renovation'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 active:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <svg className={`w-5 h-5 ${projectType === 'renovation' ? 'text-blue-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                  </svg>
                  <span className={`font-medium ${projectType === 'renovation' ? 'text-blue-700' : 'text-gray-700'}`}>
                    Renovation
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">10 phases</p>
              </button>
              <button
                type="button"
                onClick={() => setProjectType('build_from_land')}
                className={`p-4 border-2 rounded-lg text-left transition-colors min-h-[72px] touch-manipulation ${
                  projectType === 'build_from_land'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 active:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <svg className={`w-5 h-5 ${projectType === 'build_from_land' ? 'text-blue-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span className={`font-medium ${projectType === 'build_from_land' ? 'text-blue-700' : 'text-gray-700'}`}>
                    Build from Land
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">15 phases</p>
              </button>
            </div>
          </div>
        </div>

        {/* Footer - Sticky */}
        <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="w-full sm:w-auto px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 active:scale-[0.98] min-h-[48px] touch-manipulation transition-all disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || (isLowPayment && !confirmed)}
            className="w-full sm:w-auto px-4 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.98] min-h-[48px] touch-manipulation transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Creating...' : 'Create Site Entry'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateSiteEntryModal;
